import { useEffect, useRef, useState, useContext } from 'react';
import { assertExists, Popover, Tag, TagProps } from '@scalingworks/react-admin-ui';
import { createHelpers, createResource, ResourceField } from '@scalingworks/refine-react-admin';
import { FiUser } from 'react-icons/fi';
import {
  getSdk,
  type Customer,
  CreateCustomerInput,
  UpdateCustomerInput,
  Channel,
  CustomerStatus,
  ProfileConfigField,
  ProfileConfig,
} from '~/api';
import {
  ActionButton,
  ActionButtonRefProps,
  ActionModalWithTrigger,
  FormBuilder,
  Loading,
  RadioGroup,
  SomethingWentWrong,
  TriggerConfirmModal,
  InfoBox,
} from '~/components';
import {
  useCreate,
  useNavigation,
  useNotification,
  useOne,
  useTranslate,
  useUpdate,
} from '@refinedev/core';
import { parsePhoneNumber } from 'libphonenumber-js';
import { customerFormBuilder } from '~/config/Customer/form';
import { useParams } from 'react-router-dom';
import { customerEmailExist, customerPhoneExist } from '../helpers/customer';
import { isValidDate } from '../helpers/date';
import { resourceNames } from '../resource-names';
import { CustomerShowPage } from './show';
import { ResourceContext } from '../type';
import { ChannelContext } from '~/providers/Channel/context';
import { startCase } from 'lodash';

const { defineFields, defineCardSection, defineShowPage, defineFilterControls } =
  createHelpers<Customer>({
    resourceName: resourceNames.customer,
  });

const formatData = (data: any) => {
  const { birthDate, gender, preferredOutletId, ...rest } = data;
  const date = new Date(birthDate);
  return {
    ...rest,
    customFields: {
      ...(rest.customFields || {}),
      dateOfBirth: isValidDate(date) ? date : undefined,
      gender,
      preferredOutletId,
    } as CreateCustomerInput['customFields'],
  };
};

// Helper function to render the profile completion info box
const renderProfileCompletionInfoBox = (profileConfig: ProfileConfig | null | undefined, customer?: Customer) => {
  // Don't show if profile config is missing
  if (!profileConfig || !profileConfig.fields?.length) return null;
  
  // In edit mode, don't show the InfoBox if the profile is already complete
  if (customer && customer.customFields?.isProfileComplete) return null;

  // Filter fields that trigger profile completion
  const profileCompleteFields = profileConfig.fields.filter(
    (field) => field.config.triggerProfileComplete
  );
  
  // If no fields trigger profile completion, don't show the InfoBox
  if (!profileCompleteFields.length) return null;

  // Format fields as a comma-separated list
  const fieldList = profileCompleteFields.map((field) => startCase(field.key)).join(', ');

  return (
    <InfoBox title="Profile Completion Information" variant="info">
      <p>Fill up {fieldList} to complete profile</p>
    </InfoBox>
  );
};

export const CustomerStatusTagColor: Record<CustomerStatus, TagProps['color']> = {
  [CustomerStatus.Active]: 'success',
  [CustomerStatus.Inactive]: 'default',
};

export const customerFields: ResourceField<Customer>[] = [
  'id',
  'createdAt',
  'updatedAt',
  'firstName',
  'lastName',
  'phoneNumber',
  'emailAddress',
  {
    customFields: [
      'status',
      'dateOfBirth',
      'gender',
      'pay2winOn',
      'preferredOutletId',
      'isProfileComplete',
      'signupThrough',
      'referralCode',
      'referralCodeApplied',
    ],
  },
  { user: ['id'] },
];

export const customerResource = (context: ResourceContext) => {
  return createResource({
    name: resourceNames.customer,
    label: 'Customers',
    icon: <FiUser />,
    fields: defineFields(customerFields),
    defaultValues: {} as any,
    defaultPageSize: 25,
    defaultSorter: [{ field: 'createdAt', order: 'desc' }],
    allowSearch: true,
    searchConfig: {
      placeholder: ({ t }) =>
        t('customer.placeholder.search', {
          fallback: 'Search by Name, Email or Contact No.',
          ns: 'common',
        }),
    },
    createConfig: {
      title: ({ t }) =>
        t('customer.create.add', {
          fallback: 'Add Customer',
          ns: 'common',
        }),
    },
    filterConfig: {
      alwaysExpanded: true,
    },
    allowDelete: true,
    columns: ({ LinkToDetails, navigateToEdit, invokeDelete, t }) => [
      {
        id: 'name',
        header: t('customer.column.name', { fallback: 'Name', ns: 'common' }),
        cell: (data) => {
          const { id, firstName = '', lastName = '' } = data.row.original;

          return <LinkToDetails resourceId={id}>{`${firstName} ${lastName}`.trim()}</LinkToDetails>;
        },
      },
      {
        id: 'phoneNumber',
        header: t('customer.column.contact', { fallback: 'Contact No.', ns: 'common' }),
        accessorKey: 'phoneNumber',
        cell: (data) => {
          const phone = data.row.original.phoneNumber || '-';
          return (
            <LinkToDetails resourceId={data.row.original.id}>
              <span>{phone}</span>
            </LinkToDetails>
          );
        },
      },
      {
        id: 'email',
        header: t('customer.column.email', { fallback: 'Email', ns: 'common' }),
        accessorKey: 'emailAddress',
        cell: (data) => {
          const { id, emailAddress } = data.row.original;
          return <LinkToDetails resourceId={id}>{emailAddress || '-'}</LinkToDetails>;
        },
      },
      {
        id: 'status',
        header: t('customer.column.status', { fallback: 'Status', ns: 'common' }),
        cell: (data) => {
          const { id, customFields } = data?.row?.original;
          const { status } = customFields || {};

          return (
            <LinkToDetails resourceId={id}>
              <Tag color={CustomerStatusTagColor[status as CustomerStatus]}>
                {t(`customer.status.${status?.toLowerCase()}`, {
                  fallback: status as string,
                  ns: 'common',
                }).toUpperCase()}
              </Tag>
            </LinkToDetails>
          );
        },
      },
      {
        id: 'actions',
        header: () => <div />,
        accessorKey: 'id',
        enableSorting: false,
        cell: (data) => {
          const userId = data.cell.getValue<string>();
          const actionButtonRef = useRef<ActionButtonRefProps>(null);
          const { firstName, lastName, customFields, id } = data.row.original;
          const fullName = `${firstName || ''} ${lastName || ''}`;
          const { status } = customFields || {};
          const [statusVal, setStatusVal] = useState<string>(status || '');
          const [showUpdateModal, setShowUpdateModal] = useState(false);
          const { mutate } = useUpdate();
          const t = useTranslate();

          useEffect(() => {
            setStatusVal(status || '');
          }, [data?.row?.original]);

          return (
            <ActionButton
              ref={actionButtonRef}
              actions={[
                {
                  label: t('actions.edit'),
                  name: 'edit',
                  onAction: () => navigateToEdit({ id: userId }),
                },
                {
                  label: t('actions.updateStatus'),
                  name: 'updateStatus',
                  onAction: () => {
                    return;
                  },
                  render: (onAction) => {
                    return (
                      <button type="button">
                        <ActionModalWithTrigger
                          triggerText={t('actions.updateStatus')}
                          visible={showUpdateModal}
                          title={t('actions.updateStatus')}
                          renderBody={() => {
                            return (
                              <div>
                                <RadioGroup
                                  isField
                                  label={t('customer.column.status')}
                                  options={['ACTIVE', 'INACTIVE'].map((value) => ({
                                    label: t(`customer.status.selection.${value.toLowerCase()}`),
                                    value,
                                  }))}
                                  value={statusVal}
                                  onValueChange={setStatusVal}
                                />
                              </div>
                            );
                          }}
                          onOpenChange={(val) => {
                            const actionButtonSetOpen = actionButtonRef?.current?.setOpen;
                            actionButtonSetOpen?.(val);
                            setShowUpdateModal(val);
                          }}
                          onPressCancel={() => setStatusVal(status || '')}
                          onPressConfirm={() => {
                            mutate({
                              id,
                              resource: resourceNames?.customer,
                              values: {
                                status: statusVal,
                              },
                            });
                          }}
                        />
                      </button>
                    );
                  },
                },
                {
                  label: t('actions.delete'),
                  name: 'delete',
                  onAction: () => invokeDelete({ id: userId }),
                  render: (onAction) => {
                    const [show, setShow] = useState(false);
                    return (
                      <button type="button">
                        <TriggerConfirmModal
                          visible={show}
                          onOpenChange={setShow}
                          onPressConfirm={onAction}
                          description={
                            <span>
                              {t('warnings.deleteConfirmationWithName', {
                                resource: t('staff.name').toLocaleLowerCase(),
                              })}{' '}
                              <span className="font-semibold text-error-300">{`${fullName} (${t(
                                `customer.status.${status?.toLowerCase()}`
                              )})`}</span>
                              ? {t('warnings.cannotUndo')}
                            </span>
                          }
                        />
                      </button>
                    );
                  },
                },
              ]}
            />
          );
        },
      },
    ],
    dataProvider: {
      create: ({ client, variables }) => {
        const { password, customFields, ...restInput } = variables;

        return getSdk(client)
          .createCustomer({
            input: {
              ...restInput,
              customFields,
            } as CreateCustomerInput,
            password,
          })
          .then((res) => ({
            data: res.createCustomer,
          }));
      },
      update: ({ client, variables, id }) => {
        const { password, customFields, ...restInput } = variables;

        return getSdk(client)
          .updateCustomer({
            input: {
              id: id as string,
              ...restInput,
              customFields,
            } as UpdateCustomerInput,
          })
          .then((res) => {
            assertExists(res.updateCustomer, 'value returned by updateCustomer is undefined');

            return {
              data: res.updateCustomer,
            };
          });
      },
    },
    create: {
      render: (helpers) => {
        const navigateTo = useNavigation();
        const notif = useNotification();
        const t = useTranslate();
        const { mutate: createCustomer } = useCreate();
        const { channel } = useContext(ChannelContext);

        return (
          <FormBuilder
            title={t('customer.create.add')}
            resourceName={resourceNames.customer}
            header={renderProfileCompletionInfoBox(channel?.profileConfig, undefined)}
            onSubmit={async (data) => {
              const { phoneNumber } = data;
              const parsed = parsePhoneNumber(phoneNumber);
              if (!parsed.isValid()) {
                notif?.open?.({
                  message: t('messages.invalidPhoneFormat', {}, 'Invalid Phone Format'),
                  type: 'error',
                });
                return;
              }
              const payload = formatData(data);

              const phoneExisted = await customerPhoneExist(parsed.number);
              if (phoneExisted) {
                notif?.open?.({
                  message: t('messages.phoneExisted', 'Phone number already existed'),
                  type: 'error',
                });
                return;
              }

              createCustomer(
                {
                  resource: resourceNames.customer,
                  values: payload,
                },
                {
                  onSuccess: (data) => {
                    const customerId = data.data.id;
                    if (customerId) {
                      navigateTo.show(resourceNames.customer, customerId);
                    } else {
                      navigateTo.list(resourceNames.customer);
                    }
                  },
                }
              );
            }}
            items={() => customerFormBuilder()}
          />
        );
      },
    },
    edit: {
      render(helpers) {
        const { id } = useParams();
        const navigation = useNavigation();
        const t = useTranslate();
        const notif = useNotification();
        const { channel } = useContext(ChannelContext);

        if (!id) return <SomethingWentWrong />;

        const { data, isLoading } = useOne({
          resource: resourceNames.customer,
          id,
          metaData: {
            fields: customerFields,
          },
        });
        const mainData = data?.data as Customer;

        const { mutate: updateCustomer } = useUpdate();
        if (isLoading) return <Loading />;
        return (
          <FormBuilder
            isUpdate
            resourceName={resourceNames.customer}
            title={t('customer.update.name')}
            header={renderProfileCompletionInfoBox(channel?.profileConfig, mainData)}
            onSubmit={async (data) => {
              const { phoneNumber } = data;
              const parsed = parsePhoneNumber(phoneNumber);
              if (!parsed.isValid()) {
                notif?.open?.({
                  message: t('messages.invalidPhoneFormat', {}, 'Invalid Phone Format'),
                  type: 'error',
                });
                return;
              }
              const payload = formatData(data);

              if (mainData?.emailAddress !== payload.emailAddress) {
                const emailExisted = await customerEmailExist(payload.emailAddress, id);
                if (emailExisted) {
                  notif?.open?.({
                    message: t('messages.emailExisted', 'Email address already existed'),
                    type: 'error',
                  });
                  return;
                }
              }

              if (mainData?.phoneNumber !== payload.phoneNumber) {
                const phoneExisted = await customerPhoneExist(parsed.number, id);
                if (phoneExisted) {
                  notif?.open?.({
                    message: t('messages.phoneExisted', 'Phone number already existed'),
                    type: 'error',
                  });
                  return;
                }
              }

              updateCustomer(
                {
                  id,
                  resource: resourceNames.customer,
                  values: payload,
                },
                { onSuccess: () => navigation.list(resourceNames.customer) }
              );
            }}
            items={() => customerFormBuilder(mainData)}
          />
        );
      },
    },
    show: defineShowPage({
      component: (props) => {
        return <CustomerShowPage resourceId={props?.resourceId} />;
      },
    }),
  });
};
